import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getJSON, onAuthenticationRequired, patchJSON, postJSON } from '@/lib/api'
import type { MemberSignInChange, MemberSignInResponse } from './memberSignInTypes'

export function useMemberSignIn(id: number, scope: string, readOnly: boolean) {
  const client = useQueryClient()
  const path = `/api/v1/connections/${id}/member_sign_in`
  const key = ['connection-member-sign-in', id, scope]
  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await getJSON<MemberSignInResponse>(path)).member_sign_in,
    retry: false,
  })
  const [secret, setSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [dialog, setDialog] = useState<'secret' | 'link' | null>(null)
  const active = useRef(true)
  const busy = useRef(false)
  useEffect(() => {
    active.current = true
    const unsubscribe = onAuthenticationRequired(() => {
      active.current = false
      setSecret(null)
      setDialog(null)
    })
    return () => {
      active.current = false
      unsubscribe()
    }
  }, [])
  async function save(change: MemberSignInChange) {
    if (busy.current || readOnly || !query.data?.can_manage) return
    busy.current = true
    setPending(true)
    setError(null)
    try {
      const body = { member_sign_in: change }
      const response =
        'confirmed' in change
          ? await postJSON<MemberSignInResponse>(`${path}/secret`, body)
          : await patchJSON<MemberSignInResponse>(path, body)
      if (!active.current) return
      // Plaintext is local to this mounted identity. No query or mutation caches retain it.
      setSecret(response.client_secret ?? null)
      client.setQueryData(key, response.member_sign_in)
      setDialog(null)
    } catch (failure) {
      if (active.current)
        setError(failure instanceof Error ? failure.message : 'Could not update Member sign-in.')
    } finally {
      busy.current = false
      if (active.current) setPending(false)
    }
  }
  return {
    query,
    secret,
    error,
    pending,
    dialog,
    save,
    dismissSecret: () => setSecret(null),
    close: () => {
      setDialog(null)
      setError(null)
    },
    open: (kind: 'secret' | 'link') => {
      setError(null)
      setDialog(kind)
    },
  }
}
